import React, { useState, useEffect } from "react";
import { Box, Grid, TextField, Typography, FormControl, InputLabel, Select, MenuItem, Chip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { PrimaryButton, SecondaryButton } from "../../../../layout/Button/Button";
import { useBranchedStoryEdit } from "../../contexts/BranchedStoryEditContext";
import { CardColour } from "common/build/prisma/client";
import { VCard } from "../../../../../core/components/VCard";
import { list as listThemes } from "../../../../../client/api/theme";

interface ThemeOption {
    id: number;
    name: string;
    colour: string;
}

const useStyles = makeStyles(() =>
    createStyles({
        formContainer: {
            padding: "24px",
        },
        fieldGroup: {
            marginBottom: "24px",
        },
        actionButtons: {
            marginTop: "32px",
            display: "flex",
            gap: "16px",
            justifyContent: "flex-end",
        },
        themeChip: {
            margin: "4px",
        },
    }),
);

interface EditStoryDetailsProps {
    onComplete: () => void;
}

interface FormValues {
    storyName: string;
    author: string;
    synopsis: string;
    themes: number[];
    ageRange: string;
    colour: CardColour | "";
    resources: string;
    resourcesROI: string;
}

// Validation schema
const validationSchema = Yup.object().shape({
    storyName: Yup.string().required("Story name is required").max(255, "Story name must be less than 255 characters"),
    author: Yup.string().required("Author is required").max(255, "Author name must be less than 255 characters"),
    synopsis: Yup.string().max(1000, "Synopsis must be less than 1000 characters"),
    ageRange: Yup.string().max(50, "Age range must be less than 50 characters"),
    resources: Yup.string().url("Must be a valid URL").max(500, "Resource URL must be less than 500 characters"),
    resourcesROI: Yup.string().url("Must be a valid URL").max(500, "Resource URL must be less than 500 characters"),
});

// Available card colors for V4 template
const cardColors: CardColour[] = [
    "Red",
    "Orange",
    "Yellow",
    "Green",
    "Cyan",
    "Blue",
    "Magneta",
    "Crimson",
    "Silver",
    "Bronze",
    "BrightYellow",
    "Aqua",
    "Purple",
    "BrightOrange",
    "BrightRed",
    "LightGreen",
    "NightBlue",
];

export const EditStoryDetails: React.FC<EditStoryDetailsProps> = ({ onComplete }) => {
    const classes = useStyles();
    const { currentStory, updateStoryField, hasChanges, loading, error, saveChanges } = useBranchedStoryEdit();
    const [saving, setSaving] = useState(false);
    const [availableThemes, setAvailableThemes] = useState<ThemeOption[]>([]);

    useEffect(() => {
        listThemes({})
            .then((res) => {
                setAvailableThemes(res.items || []);
            })
            .catch(() => {
                setAvailableThemes([]);
            });
    }, []);

    // Build age range options
    const ageRanges: string[] = [];
    for (let i = 4; i <= 17; i++) {
        ageRanges.push(`${i}-${i + 1}`);
    }
    ageRanges.push("18+");

    const initialValues: FormValues = {
        storyName: currentStory.storyName || "",
        author: currentStory.author || "",
        synopsis: currentStory.synopsis || "",
        themes: currentStory.Themes?.map((t) => t.id) || [],
        ageRange: currentStory.ageRange || "",
        colour: currentStory.colour || "",
        resources: currentStory.resources || "",
        resourcesROI: currentStory.resourcesROI || "",
    };

    const handleSubmit = async (values: FormValues) => {
        // Update all fields in the context
        updateStoryField("storyName", values.storyName);
        updateStoryField("author", values.author);
        updateStoryField("synopsis", values.synopsis);
        updateStoryField("ageRange", values.ageRange);
        updateStoryField("colour", values.colour as CardColour);
        updateStoryField("resources", values.resources);
        updateStoryField("resourcesROI", values.resourcesROI);
        updateStoryField(
            "Themes",
            values.themes.map((id) => {
                const t = availableThemes.find((th) => th.id === id);
                return { id, name: t?.name || "", colour: t?.colour || "" };
            }),
        );

        // Save changes if there are any, then proceed to next step
        setSaving(true);
        try {
            if (hasChanges) {
                await saveChanges();
            }
            // Always proceed to next step regardless of whether changes were saved
            onComplete();
        } catch (err) {
            // Error is handled by the context
            console.error("Failed to save story details:", err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <VCard>
            <Box className={classes.formContainer}>
                <Typography variant="h4" gutterBottom>
                    Edit Story Details
                </Typography>

                {error && (
                    <Box mb={2}>
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    </Box>
                )}

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ errors, touched, values, setFieldValue }) => (
                        <Form>
                            <Grid container spacing={3}>
                                {/* Story Name */}
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="storyName"
                                        label="Story Name *"
                                        fullWidth
                                        variant="outlined"
                                        error={touched.storyName && !!errors.storyName}
                                        helperText={touched.storyName && errors.storyName}
                                    />
                                </Grid>

                                {/* Author */}
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="author"
                                        label="Author *"
                                        fullWidth
                                        variant="outlined"
                                        error={touched.author && !!errors.author}
                                        helperText={touched.author && errors.author}
                                    />
                                </Grid>

                                {/* Age Range */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>Age Range</InputLabel>
                                        <Select
                                            name="ageRange"
                                            value={values.ageRange}
                                            onChange={(e) => setFieldValue("ageRange", e.target.value)}
                                            label="Age Range"
                                        >
                                            <MenuItem value="">
                                                <em>Select age range</em>
                                            </MenuItem>
                                            {ageRanges.map((range) => (
                                                <MenuItem key={range} value={range}>
                                                    {range}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Card Color */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>Card Color</InputLabel>
                                        <Select
                                            variant="outlined"
                                            name="colour"
                                            value={values.colour}
                                            onChange={(e) => setFieldValue("colour", e.target.value)}
                                            label="Card Color"
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {cardColors.map((color) => (
                                                <MenuItem key={color} value={color}>
                                                    {color}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Synopsis */}
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="synopsis"
                                        label="Story Synopsis"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        placeholder="Brief description of the story..."
                                        error={touched.synopsis && !!errors.synopsis}
                                        helperText={touched.synopsis && errors.synopsis}
                                    />
                                </Grid>

                                {/* Resource Links */}
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="resources"
                                        label="Additional Resources Link - NI"
                                        fullWidth
                                        variant="outlined"
                                        placeholder="https://..."
                                        error={touched.resources && !!errors.resources}
                                        helperText={touched.resources && errors.resources}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="resourcesROI"
                                        label="Additional Resources Link - ROI"
                                        fullWidth
                                        variant="outlined"
                                        placeholder="https://..."
                                        error={touched.resourcesROI && !!errors.resourcesROI}
                                        helperText={touched.resourcesROI && errors.resourcesROI}
                                    />
                                </Grid>

                                {/* Themes */}
                                <Grid item xs={12}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel id="edit-themes-label">Themes</InputLabel>
                                        <Select
                                            labelId="edit-themes-label"
                                            multiple
                                            value={values.themes}
                                            onChange={(e) => setFieldValue("themes", e.target.value)}
                                            label="Themes"
                                            renderValue={(selected) => (
                                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                    {(selected as number[]).map((themeId) => {
                                                        const theme = availableThemes.find((t) => t.id === themeId);
                                                        return (
                                                            <Chip
                                                                key={themeId}
                                                                label={theme?.name || `Theme ${themeId}`}
                                                                size="small"
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {availableThemes.map((theme) => (
                                                <MenuItem key={theme.id} value={theme.id}>
                                                    {theme.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* Action Buttons */}
                            <Box className={classes.actionButtons}>
                                <SecondaryButton type="button" disabled={saving || loading}>
                                    Cancel
                                </SecondaryButton>
                                <PrimaryButton type="submit" disabled={saving || loading}>
                                    {saving ? "Saving..." : hasChanges ? "Save & Continue" : "Continue"}
                                </PrimaryButton>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        </VCard>
    );
};

// Display name for debugging (following design guide)
EditStoryDetails.displayName = "EditStoryDetails";
